import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Loading } from 'react-style-guide';
import SearchResultHeader from '../components/SearchResultHeader';
import GridsInfinitScroll from '../../gameGrid/containers/GridsInfinitScroll';
import gamesService from '../../gameData/services/gamesService';
import gamesApiConstants from '../../gameData/constants/gamesApiConstants';
import searchResultLayout from '../constants/searchResultLayoutConstants';
import gamesPageLayout from '../../gameCarousel/constants/layoutConstants';
import {
  useGameImpressionGridTracker,
  grid
} from '../../../../ts/react/common/hooks/useGameImpressionGridTracker';
import { queryParams } from '../../../../ts/react/common/constants/browserConstants';
import { getSession } from '../../../../ts/react/common/utils/browserUtils';
import searchHelper from '../utils/searchHelper';

function SearchResult(props) {
  const { translate } = props;
  const keywordFromUrl = searchHelper.getKeyword();
  const sessionFromUrl = getSession(queryParams.gameSearchQuerySession);
  const [originalKeyword, setOriginalKeyword] = useState(keywordFromUrl);
  const [gamesData, setGamesData] = useState([]);
  const [metaData, setMetaData] = useState(undefined);
  const [correctedKeyword, setCorrectedKeyword] = useState(null);
  const [filteredKeyword, setFilteredKeyword] = useState(null);
  const [featureUniverseId, setFeatureUniverseId] = useState(null);
  const [suggestedKeyword, setSuggestedKeyword] = useState(null);
  const eventRef = useRef(null);
  const { params } = gamesApiConstants;
  const initializedSearchApiParams = {
    keyword: params.keyword,
    startRows: params.startRows,
    maxRows: params.maxRows,
    hasMoreRows: true,
    isKeywordSuggestionEnabled: params.isKeywordSuggestionEnabled
  };
  const [searchApiParams, setSearchApiParams] = useState(initializedSearchApiParams);
  const [isDataFetching, setDataFetching] = useState(false);
  const [isSearchResultEmpty, setSearchResultEmpty] = useState(false);

  const getGamesSearchResult = (keyword, isKeywordSuggestionEnabled) => {
    if (searchApiParams.hasMoreRows) {
      setDataFetching(true);
      setSearchResultEmpty(false);
      searchApiParams.keyword = keyword;
      searchApiParams.isKeywordSuggestionEnabled = isKeywordSuggestionEnabled;

      gamesService
        .getGamesList(searchApiParams)
        .then(
          result => {
            setCorrectedKeyword(result?.correctedKeyword);
            setFeatureUniverseId(result?.featuredSearchUniverseId);
            setSuggestedKeyword(result?.suggestedKeyword);
            if (result?.filteredKeyword) {
              setFilteredKeyword(result?.filteredKeyword);
            }

            const { games, ...parsedMetaData } = result;
            const updatedApiParams = gamesService.buildUpdatedApiParams(result, searchApiParams);
            setMetaData(parsedMetaData);
            setSearchApiParams(currentApiParams =>
              Object.assign(currentApiParams, updatedApiParams)
            );
            if (result?.games && result?.games?.length) {
              setGamesData(currentGamesData => {
                const updateGamesData = currentGamesData.concat(result.games);
                return updateGamesData;
              });
            } else {
              setSearchResultEmpty(true);
            }
          },
          () => {
            setSearchApiParams(currentSortApiParams =>
              Object.assign(currentSortApiParams, { hasMoreRows: false })
            );
          }
        )
        .finally(() => {
          setDataFetching(false);
        });
    }
  };

  const handleSearchRevertedKeyword = keyword => {
    // should clean out params for original keywork search
    setGamesData([]);
    searchApiParams.hasMoreRows = true;
    searchApiParams.startRows = 0;

    getGamesSearchResult(keyword, false);
  };

  const handleSearchSuggestedKeyword = keyword => {
    // should clean out params for original keywork search
    setGamesData([]);
    searchApiParams.hasMoreRows = true;
    searchApiParams.startRows = 0;

    setOriginalKeyword(keyword);
  };

  useEffect(() => {
    getGamesSearchResult(originalKeyword, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalKeyword]);

  useGameImpressionGridTracker(
    eventRef,
    gamesData,
    metaData,
    keywordFromUrl,
    sessionFromUrl,
    grid.calculateViewedGameData
  );

  return (
    <div className='games-list-container'>
      <SearchResultHeader
        correctedKeyword={correctedKeyword}
        originalKeyword={originalKeyword}
        filteredKeyword={filteredKeyword}
        suggestedKeyword={suggestedKeyword}
        searchRevertedKeyword={handleSearchRevertedKeyword}
        searchSuggestedKeyword={handleSearchSuggestedKeyword}
        {...props}
      />
      {gamesData.length > 0 && (
        <GridsInfinitScroll
          eventRef={eventRef}
          gamesData={gamesData}
          featureUniverseId={featureUniverseId}
          pageType={gamesPageLayout.pageType.gameSearch}
          apiParams={searchApiParams}
          {...props}
        />
      )}
      {isSearchResultEmpty && (
        <div className='search-result-empty-container font-bold'>
          {translate(searchResultLayout.emptyResultLabel)}
        </div>
      )}
      {isDataFetching ? <Loading /> : null}
    </div>
  );
}

SearchResult.propTypes = {
  translate: PropTypes.func.isRequired
};

export default SearchResult;
